import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AppUseDispatch, AppUseSelector } from '../../../redux/hooks'
import { SET_LEARN_ABOUT_ANSWER, SET_LEARN_ABOUT_TYPE } from '../../../redux/types'
import { getLearnAboutMaayot, getLearnAboutMaayotTitle, saveAttribution } from '../../../redux/actions/onboardingAction'
import { trackOnboarding } from '../../../utils/analytics'
import { StepTitle, StepSubtitle } from '../stepContext'

/**
 * One compulsory screen replacing the two mandatory survey screens that used to
 * sit at the top of the funnel. The detail field matters: maayot sponsors
 * podcasts/newsletters/accounts and uses it for attribution and LTV.
 */
const AttributionStep = ({ next }: { next: () => void }) => {
  const { t } = useTranslation('onboarding')
  const dispatch = AppUseDispatch()
  const { learnAbout, learnAboutType, learnAboutTitle } = AppUseSelector(({ onboarding }) => onboarding)
  const [answer, setAnswer] = useState('')
  const [saving, setSaving] = useState(false)
  const [sourcesFailed, setSourcesFailed] = useState(false)

  // Pairs with attribution_submitted to measure abandonment at this question.
  // It is compulsory, so the number that matters is how many people it turns
  // away: reaching it and never answering means leaving signup over a question
  // there is no way past.
  useEffect(() => {
    trackOnboarding('attribution_viewed', {})
  }, [])

  useEffect(() => {
    if (learnAbout?.length) return
    // The question is compulsory and has no skip, so an empty list is not a
    // cosmetic problem — it is a dead end with nothing to click. Track the
    // failure so the step can offer a way out.
    getLearnAboutMaayot().then((res: any) => {
      if (!res?.data?.length) setSourcesFailed(true)
    })
  }, [])

  const save = async (source: string, detail: string) => {
    dispatch({ type: SET_LEARN_ABOUT_ANSWER, payload: detail })
    await saveAttribution({
      how_learn_about_maayot: source,
      learn_about_question: learnAboutTitle?.question,
      learn_about_answer: detail || undefined,
    })
    trackOnboarding('attribution_submitted', { source, has_detail: Boolean(detail) })
  }

  const selectSource = async (source: string) => {
    dispatch({ type: SET_LEARN_ABOUT_TYPE, payload: source })
    setAnswer('')
    await getLearnAboutMaayotTitle({ source })
    // This is the last question on its screen, so it must not own an advance
    // button of its own — the group's Continue does that. Picking a source is
    // the answer, and the optional detail below saves on blur.
    await save(source, '')
    next()
  }

  // The sources-failed escape hatch below is the only way past this question
  // without answering it; the question itself offers none.
  const skip = async () => {
    if (saving) return
    setSaving(true)
    trackOnboarding('attribution_skipped', {})
    setSaving(false)
    next()
  }

  return (
    <div className='ob-step'>
      <StepTitle>{t('attribution.title')}</StepTitle>
      <StepSubtitle>{t('attribution.subtitle')}</StepSubtitle>

      <div className='ob-chips'>
        {(learnAbout || []).map((source: string) => (
          <button
            key={source}
            type='button'
            className={`ob-chip ${learnAboutType === source ? 'is-selected' : ''}`}
            onClick={() => selectSource(source)}
          >
            {source}
          </button>
        ))}
      </div>

      {learnAboutType && learnAboutTitle?.question ? (
        <div className='ob-attr-detail'>
          <label htmlFor='ob-attr-input'>{learnAboutTitle.question}</label>
          <input
            id='ob-attr-input'
            type='text'
            className='ob-input'
            placeholder={learnAboutTitle?.placeholder || t('attribution.placeholder')}
            value={answer}
            maxLength={80}
            onChange={(e) => setAnswer(e.target.value)}
            onBlur={learnAboutType ? () => save(learnAboutType, answer.trim()) : undefined}
          />
        </div>
      ) : null}

      {/* Compulsory. The only way past without answering is a failure to load
          the sources, which must not strand the user one question short of
          finishing their signup. Otherwise the group's Continue advances, so
          this step owns no button of its own. */}
      {sourcesFailed && !learnAbout?.length ? (
        <div className='ob-actions'>
          <button type='button' className='ob-cta-ghost' onClick={skip} disabled={saving}>
            {t('attribution.skip')}
          </button>
        </div>
      ) : null}
    </div>
  )
}

export default AttributionStep
